import React, { useEffect, useState } from "react";

import { useTranslation } from "lib/i18n/use-translation";
import { Button } from "components/ui/Button";
import { ReactComponent as OfflineIcon } from "Assets/icon/icon-offline.svg";
import { ReactComponent as ErrorIcon } from "Assets/icon/icon-error.svg";

import styles from "./boot-error.module.scss";

/**
 * Full-screen boot fallback shown when the cold-start auth probe fails
 * transiently (offline / 5xx / timeout / CORS). Mirrors the mobile
 * ``screens/Error.tsx`` recovery screen — an offline probe decides
 * between an "offline" and a generic "something went wrong" state, and
 * a single "Try again" action re-runs the probe. A genuinely
 * unrecoverable session (expired refresh token) routes to sign-in
 * instead and never reaches here.
 */

// Same probe target mobile uses — a 204 endpoint that succeeds cheaply
// when online and throws (network error) when offline, letting us tell
// "no connection" apart from "server hiccup" without trusting a single
// signal. ``navigator.onLine`` is checked first as a fast negative.
const OFFLINE_PROBE_URL = "https://clients3.google.com/generate_204";

const checkIsOffline = async (): Promise<boolean> => {
  if (typeof navigator !== "undefined" && navigator.onLine === false) {
    return true;
  }
  try {
    await fetch(OFFLINE_PROBE_URL, {
      method: "HEAD",
      cache: "no-store",
      mode: "no-cors",
    });
    return false;
  } catch {
    return true;
  }
};

interface BootErrorProps {
  onRetry: () => Promise<unknown> | unknown;
}

const BootError: React.FC<BootErrorProps> = ({ onRetry }) => {
  const { t } = useTranslation(["common"]);
  // Renders the generic error state until the probe resolves — the
  // unresolved window looks identical to "online", so no tri-state.
  const [offline, setOffline] = useState(false);
  const [isRetrying, setIsRetrying] = useState(false);

  useEffect(() => {
    let cancelled = false;
    checkIsOffline().then((result) => {
      if (!cancelled) setOffline(result);
    });
    return () => {
      cancelled = true;
    };
  }, []);

  const handleRetry = async () => {
    if (isRetrying) return;
    setIsRetrying(true);
    try {
      await onRetry();
    } finally {
      setIsRetrying(false);
    }
  };

  const Icon = offline ? OfflineIcon : ErrorIcon;
  const iconSize = offline ? 29 : 33;
  const title = offline
    ? t("common:error.offline.title")
    : t("common:error.generic.title");
  const subtitle = offline
    ? t("common:error.offline.subtitle")
    : t("common:error.generic.subtitle");

  return (
    <div className={styles["container"]}>
      <div className={styles["content"]}>
        <div className={styles["icon-badge"]}>
          <Icon width={iconSize} height={iconSize} aria-hidden />
        </div>
        <div className={styles["text-stack"]}>
          <p className={styles["title"]}>{title}</p>
          <p className={styles["subtitle"]}>{subtitle}</p>
        </div>
        <Button
          variant="auth-primary"
          className={styles["retry-button"]}
          onClick={handleRetry}
          loading={isRetrying}
        >
          {t("common:action.tryAgain")}
        </Button>
      </div>
    </div>
  );
};

export default BootError;
